import React, {useCallback, useEffect, useState, useRef, useMemo} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Layout} from '../../components/layout';
import {Button, Toast, KeyboardShortcuts} from '../../components';
import {voyageService, carService, printService} from '../../services';
import {useToast} from '../../hooks/useToast';
import {useFilterPreference} from '../../hooks/useFilterPreference';
import {BulkLengthPricingPayload, BulkPricingUpdatePayload, Car, Voyage} from '../../types';
import {attachVoyageCodes} from '../../utils/carUtils';
import {formatDateOrToday} from '../../utils/tableUtils';
import {CarsManagementView} from './components/CarsManagementView';
import {FilterOption} from '../../components/tables/DataTable';

interface VoyageDetailsNavState {
    from?: {
        pathname: string;
        state?: {
            searchQuery?: string;
            searchColumn?: string;
            selectedRowId?: string;
            loadedCount?: number;
        };
    };
    // For returning from car entry page
    selectedCarId?: string;
    carsLoadedCount?: number;
    // Sort state restoration
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc';
}

const CARS_PAGE_SIZE = 200;

// Helper to get/set sort preferences from localStorage
const getSortPrefsKey = (voyageId: string) => `table-prefs-cars-table-${voyageId}`;

const loadSortPreferences = (voyageId: string): { column: string | null; direction: 'asc' | 'desc' } | null => {
    try {
        const stored = localStorage.getItem(getSortPrefsKey(voyageId));
        if (!stored) return null;
        const prefs = JSON.parse(stored);
        if (prefs.sort) {
            return {column: prefs.sort.column, direction: prefs.sort.direction};
        }
        return null;
    } catch {
        return null;
    }
};

const saveSortPreferences = (voyageId: string, column: string | null, direction: 'asc' | 'desc') => {
    try {
        const key = getSortPrefsKey(voyageId);
        const stored = localStorage.getItem(key);
        const prefs = stored ? JSON.parse(stored) : {
            version: 1,
            columnKeys: [],
            columnWidths: {},
            columnOrder: [],
            filters: {}
        };
        prefs.sort = column ? {column, direction} : null;
        localStorage.setItem(key, JSON.stringify(prefs));
    } catch {
        // Ignore localStorage errors
    }
};

const VoyageDetailsPage: React.FC = () => {
    const {voyageId} = useParams<{ voyageId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = (location.state as VoyageDetailsNavState | null) ?? null;
    const backLink = locationState?.from;

    const [voyage, setVoyage] = useState<Voyage | null>(null);
    const [cars, setCars] = useState<Car[]>([]);
    const [totalCars, setTotalCars] = useState(0);
    const [loading, setLoading] = useState(true);
    const [carsLoading, setCarsLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [updatingCarIds, setUpdatingCarIds] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<'cars' | 'statistics'>('cars');
    const [allCars, setAllCars] = useState<Car[] | null>(null); // All cars for statistics (fetched on demand)
    const [allCarsLoading, setAllCarsLoading] = useState(false);
    const [statisticsView, setStatisticsView] = useState<'shippers' | 'ports' | 'lineMeter'>('shippers');
    const [lineMeterSummaryOnly, setLineMeterSummaryOnly] = useState(false);
    const [statsSortKey, setStatsSortKey] = useState<string>('name');
    const [statsSortDir, setStatsSortDir] = useState<'asc' | 'desc'>('asc');
    const [generatingPdf, setGeneratingPdf] = useState(false);
    // Sort state for cars table (server-side sorting) - restore from localStorage, navigation state, or default to ref_no
    const [carsSortColumn, setCarsSortColumn] = useState<string | null>(() => {
        // Priority: navigation state > localStorage > default
        if (locationState?.sortColumn !== undefined) return locationState.sortColumn;
        if (voyageId) {
            const stored = loadSortPreferences(voyageId);
            if (stored) return stored.column;
        }
        return 'ref_no';
    });
    const [carsSortDirection, setCarsSortDirection] = useState<'asc' | 'desc'>(() => {
        if (locationState?.sortDirection) return locationState.sortDirection;
        if (voyageId) {
            const stored = loadSortPreferences(voyageId);
            if (stored) return stored.direction;
        }
        return 'asc';
    });
    const {toast, showToast, hideToast} = useToast();
    const isLoadingRef = useRef(false);
    const isCarsLoadingRef = useRef(false);
    // Column filters for server-side filtering (persisted to localStorage)
    const {filters: columnFilters, setFilters: setColumnFilters} = useFilterPreference({
        tableId: voyageId ? `cars-table-${voyageId}` : 'cars-table',
    });

    // Store selectedCarId in state to preserve it after location state is cleared
    const [scrollToCarId, setScrollToCarId] = useState<string | undefined>(locationState?.selectedCarId);

    const hasMoreCars = cars.length < totalCars;

    const handleBackToPrevious = () => {
        if (backLink?.pathname) {
            navigate(backLink.pathname, {state: backLink.state});
            return;
        }

        // Navigate to bookings or voyages based on the voyage type
        navigate(voyage?.is_booking ? '/bookings' : '/voyages');
    };

    useEffect(() => {
        if (!voyageId) return;

        // Check for success message from car entry page
        const successMessage = sessionStorage.getItem('carEntrySuccess');
        if (successMessage) {
            sessionStorage.removeItem('carEntrySuccess');
            showToast(successMessage, 'success');
        }

        // Single load that handles both initial mount and return from car entry
        loadVoyageAndCars(undefined, undefined, locationState?.carsLoadedCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voyageId]);

    // Clear location state after consuming it to prevent re-scroll on browser navigation
    useEffect(() => {
        if (locationState?.selectedCarId) {
            navigate(location.pathname, {replace: true, state: undefined});
            // Clear the scrollToCarId state after scroll completes (matches the 2s highlight duration in CarsTable)
            const timer = setTimeout(() => {
                setScrollToCarId(undefined);
            }, 2500);
            return () => clearTimeout(timer);
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch all cars for statistics when Statistics tab is activated
    useEffect(() => {
        if (activeTab !== 'statistics' || !voyageId || allCars !== null) return;

        const fetchAllCars = async () => {
            setAllCarsLoading(true);
            try {
                const response = await carService.getByVoyage(voyageId, {
                    skip: 0,
                    limit: 1500,
                });
                const mapped = attachVoyageCodes(response.data, new Map([[voyageId, voyage?.voyage_code || '']]));
                setAllCars(mapped);
            } catch (error: any) {
                console.error('Error loading all cars for statistics:', error);
                showToast('Failed to load statistics data', 'error');
            } finally {
                setAllCarsLoading(false);
            }
        };

        fetchAllCars();
    }, [activeTab, voyageId, allCars, voyage?.voyage_code, showToast]);

    const loadVoyageAndCars = async (sortCol?: string | null, sortDir?: 'asc' | 'desc', initialLoadCount?: number, filters?: Record<string, string[]>) => {
        if (isLoadingRef.current) return;

        try {
            isLoadingRef.current = true;
            setLoading(true);

            // Use provided sort params or current state
            const sortColumn = sortCol !== undefined ? sortCol : carsSortColumn;
            const sortDirection = sortDir !== undefined ? sortDir : carsSortDirection;
            // Use provided filters or current state
            const activeFilters = filters !== undefined ? filters : columnFilters;

            // Load more cars initially if restoring from navigation
            const effectiveLimit = (initialLoadCount && initialLoadCount > CARS_PAGE_SIZE)
                ? initialLoadCount
                : CARS_PAGE_SIZE;

            const [voyageData, carsResponse] = await Promise.all([
                voyageService.getById(voyageId!),
                carService.getByVoyage(voyageId!, {
                    skip: 0,
                    limit: effectiveLimit,
                    filters: activeFilters,
                    ...(sortColumn && {sort_column: sortColumn, sort_direction: sortDirection})
                })
            ]);

            setVoyage(voyageData);
            setTotalCars(carsResponse.total);
            const mappedCars = attachVoyageCodes(carsResponse.data, new Map([[voyageData.id, voyageData.voyage_code]]));
            setCars(mappedCars);
            setAllCars(null); // Invalidate stats cache
        } catch (error: any) {
            console.error('Error loading voyage details:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to load voyage details';
            showToast(errorMessage, 'error');
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    };

    // Load only cars (for sorting/searching without reloading the entire page)
    const loadCarsOnly = async (sortCol: string, sortDir: 'asc' | 'desc', searchQuery?: string, searchCol?: string, filters?: Record<string, string[]>) => {
        if (isCarsLoadingRef.current || !voyageId || !voyage) return;

        try {
            isCarsLoadingRef.current = true;
            setCarsLoading(true);

            // Use provided filters or current state
            const activeFilters = filters !== undefined ? filters : columnFilters;

            const carsResponse = await carService.getByVoyage(voyageId, {
                skip: 0,
                limit: CARS_PAGE_SIZE,
                filters: activeFilters,
                ...(sortCol && {sort_column: sortCol, sort_direction: sortDir}),
                ...(searchQuery && searchCol && {query: searchQuery, column: searchCol})
            });

            setTotalCars(carsResponse.total);
            const mappedCars = attachVoyageCodes(carsResponse.data, new Map([[voyage.id, voyage.voyage_code]]));
            setCars(mappedCars);
            setAllCars(null); // Invalidate stats cache
        } catch (error: any) {
            console.error('Error loading cars:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to load cars';
            showToast(errorMessage, 'error');
        } finally {
            isCarsLoadingRef.current = false;
            setCarsLoading(false);
        }
    };

    // Handle search from CarsManagementView
    const handleCarsSearch = (query: string, column: string) => {
        loadCarsOnly(carsSortColumn || 'ref_no', carsSortDirection, query, column);
    };

    // Load more cars (pagination)
    const loadMoreCars = async () => {
        if (loadingMore || !voyageId || !voyage || !hasMoreCars) return;

        try {
            setLoadingMore(true);

            const carsResponse = await carService.getByVoyage(voyageId, {
                skip: cars.length,
                limit: CARS_PAGE_SIZE,
                filters: columnFilters,
                ...(carsSortColumn && {sort_column: carsSortColumn, sort_direction: carsSortDirection})
            });

            setTotalCars(carsResponse.total);
            const mappedCars = attachVoyageCodes(carsResponse.data, new Map([[voyage.id, voyage.voyage_code]]));
            // ponytail: dedup by id on append — guards against an overlapping page
            // window so a row never renders twice. Real ordering fix is the stable
            // backend sort; this is belt-and-suspenders.
            setCars(prev => {
                const seen = new Set(prev.map(c => c.id));
                return [...prev, ...mappedCars.filter(c => !seen.has(c.id))];
            });
        } catch (error: any) {
            console.error('Error loading more cars:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to load more cars';
            showToast(errorMessage, 'error');
        } finally {
            setLoadingMore(false);
        }
    };

    // Handle sort change for cars table
    const handleCarsSort = (column: string, direction: 'asc' | 'desc') => {
        setCarsSortColumn(column);
        setCarsSortDirection(direction);
        // Persist sort preference to localStorage
        if (voyageId) {
            saveSortPreferences(voyageId, column, direction);
        }
        // Only reload cars, not the entire page
        loadCarsOnly(column, direction);
    };

    const handleAddCarEntry = () => {
        if (!voyageId) return;
        navigate(`/car-entry/${voyageId}`, {
            state: {
                from: {
                    pathname: location.pathname,
                    state: {
                        carsLoadedCount: cars.length,
                        sortColumn: carsSortColumn,
                        sortDirection: carsSortDirection
                    }
                }
            }
        });
    };

    const handleCarDoubleClick = (car: Car) => {
        if (!voyageId) return;
        navigate(`/car-entry/${voyageId}/${car.id}`, {
            state: {
                from: {
                    pathname: location.pathname,
                    state: {
                        selectedCarId: car.id,
                        carsLoadedCount: cars.length,
                        sortColumn: carsSortColumn,
                        sortDirection: carsSortDirection
                    }
                }
            }
        });
    };

    const handleEdit = (car: Car) => {
        if (!voyageId) return;
        navigate(`/car-entry/${voyageId}/${car.id}`, {
            state: {
                from: {
                    pathname: location.pathname,
                    state: {
                        selectedCarId: car.id,
                        carsLoadedCount: cars.length,
                        sortColumn: carsSortColumn,
                        sortDirection: carsSortDirection
                    }
                }
            }
        });
    };

    const handleDelete = async (car: Car) => {
        if (!window.confirm('Are you sure you want to delete this car?')) {
            return;
        }

        try {
            await carService.delete(car.id);
            await loadVoyageAndCars();
            showToast('Car deleted successfully', 'success');
        } catch (error: any) {
            console.error('Error deleting car:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to delete car';
            showToast(errorMessage, 'error');
        }
    };

    const handleBulkDelete = async (carIds: string[]) => {
        try {
            const result = await carService.bulkDelete(carIds);
            await loadVoyageAndCars();
            const count = result?.deleted_count ?? carIds.length;
            showToast(`Deleted ${count} car${count === 1 ? '' : 's'}`, 'success');
        } catch (error) {
            console.error('Error deleting cars:', error);
            throw error;
        }
    };

    const handleChangeVoyage = async (carIds: string[], newVoyageId: string) => {
        try {
            const result = await carService.bulkUpdateVoyage(carIds, newVoyageId);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            const skippedCount = result?.skipped_loaded?.length ?? 0;
            if (skippedCount > 0) {
                showToast(`Moved ${count} car${count === 1 ? '' : 's'}. ${skippedCount} loaded car${skippedCount === 1 ? ' was' : 's were'} skipped.`, 'warning');
            } else {
                showToast(`Moved ${count} car${count === 1 ? '' : 's'} to the selected voyage`, 'success');
            }
        } catch (error) {
            console.error('Error changing voyage:', error);
            throw error;
        }
    };

    const handleChangeConsignee = async (carIds: string[], consigneeId: string) => {
        try {
            const result = await carService.bulkUpdateConsignee(carIds, consigneeId);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            showToast(`Updated consignee for ${count} car${count === 1 ? '' : 's'}`, 'success');
        } catch (error) {
            console.error('Error changing consignee:', error);
            throw error;
        }
    };

    const handleChangeDestination = async (carIds: string[], portId: string) => {
        try {
            const result = await carService.bulkUpdatePort(carIds, portId);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            showToast(`Updated destination for ${count} car${count === 1 ? '' : 's'}`, 'success');
        } catch (error) {
            console.error('Error changing destination:', error);
            throw error;
        }
    };

    const handleUpdatePricing = async (carIds: string[], payload: BulkPricingUpdatePayload) => {
        try {
            const result = await carService.bulkUpdatePricing(carIds, payload);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            showToast(`Updated pricing for ${count} car${count === 1 ? '' : 's'}`, 'success');
        } catch (error) {
            console.error('Error updating pricing:', error);
            throw error;
        }
    };

    const handleSetLengthPricing = async (carIds: string[], payload: BulkLengthPricingPayload) => {
        try {
            const result = await carService.bulkSetLengthPricing(carIds, payload);
            await loadVoyageAndCars();
            const updated = result?.updated_count ?? carIds.length;
            const skipped = result?.skipped_count ?? 0;
            const baseMessage = `Updated length-based pricing for ${updated} car${updated === 1 ? '' : 's'}`;
            if (skipped > 0) {
                showToast(`${baseMessage}; skipped ${skipped} due to missing lengths`, 'warning');
            } else {
                showToast(baseMessage, 'success');
            }
        } catch (error) {
            console.error('Error applying length-based pricing:', error);
            throw error;
        }
    };

    const handleBulkSetLoaded = async (carIds: string[], loaded: boolean) => {
        try {
            // Filter out related cars from bulk operation
            // If the car isn't loaded locally (select-all-across-pages), include it — only loaded related cars are excluded
            const mainCarIds = carIds.filter(id => {
                const car = cars.find(c => c.id === id);
                return !car || !car.attachment_status;
            });

            if (mainCarIds.length === 0) {
                showToast('No main cars selected (related cars inherit loaded status)', 'info');
                return;
            }

            await carService.bulkSetLoaded(mainCarIds, loaded);
            await loadVoyageAndCars();
            const count = mainCarIds.length;
            showToast(`Marked ${count} car${count === 1 ? '' : 's'} as ${loaded ? 'loaded' : 'unloaded'}`, 'success');
        } catch (error) {
            console.error('Error updating loaded status:', error);
            throw error;
        }
    };

    const handleCombineBl = async (carIds: string[]) => {
        try {
            const result = await carService.combineBl(carIds);
            await loadVoyageAndCars();
            showToast(`Combined ${result.updated_count} units onto ${result.bl_number}`, 'success');
        } catch (error) {
            console.error('Error combining BL:', error);
            throw error;
        }
    };

    const handleSeparateBl = async (carIds: string[]) => {
        try {
            const result = await carService.separateBl(carIds);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            showToast(`Separated ${count} unit${count === 1 ? '' : 's'} from their BL`, 'success');
        } catch (error) {
            console.error('Error separating BL:', error);
            throw error;
        }
    };

    const handleAddInsurance = async (carIds: string[]) => {
        try {
            const result = await carService.bulkAddInsurance(carIds);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? carIds.length;
            showToast(`Added insurance to ${count} unit${count === 1 ? '' : 's'}`, 'success');
        } catch (error) {
            console.error('Error adding insurance fees:', error);
            throw error;
        }
    };

    const handleRemoveInsurance = async (carIds: string[]) => {
        try {
            const result = await carService.bulkRemoveInsurance(carIds);
            await loadVoyageAndCars();
            const count = result?.updated_count ?? 0;
            if (count === 0) {
                showToast('None of the selected units had insurance', 'info');
            } else {
                showToast(`Removed insurance from ${count} unit${count === 1 ? '' : 's'}`, 'success');
            }
        } catch (error) {
            console.error('Error removing insurance fees:', error);
            throw error;
        }
    };

    const handleToggleLoaded = async (car: Car, newValue: boolean) => {
        if (!voyage) return;

        // Don't allow toggling loaded status for related cars
        if (car.attachment_status) {
            return;
        }

        const delta = newValue === car.loaded ? 0 : newValue ? 1 : -1;

        setUpdatingCarIds(prev => {
            const next = new Set(prev);
            next.add(car.id);
            return next;
        });

        try {
            const updatedCar = await carService.update(car.id, {loaded: newValue}) as Car;
            const [carWithVoyageCode] = attachVoyageCodes(
                [updatedCar],
                new Map([[voyage.id, voyage.voyage_code]])
            );

            setCars(prev => prev.map(existingCar => (
                existingCar.id === car.id && carWithVoyageCode
                    ? {...existingCar, ...carWithVoyageCode}
                    : existingCar
            )));
            setAllCars(null); // Invalidate stats cache

            // Only update total_loaded if this is a main car (not related)
            if (delta !== 0 && !car.attachment_status) {
                setVoyage(prev => {
                    if (!prev) return prev;
                    const nextTotalLoaded = Math.max(0, (prev.total_loaded ?? 0) + delta);
                    return {...prev, total_loaded: nextTotalLoaded};
                });
            }
        } catch (error: any) {
            console.error('Error updating loaded status:', error);
            const errorMessage = error?.response?.data?.detail || 'Failed to update loaded status';
            showToast(errorMessage, 'error');
        } finally {
            setUpdatingCarIds(prev => {
                const next = new Set(prev);
                next.delete(car.id);
                return next;
            });
        }
    };

    // Use allCars for statistics (all cars in voyage), fall back to paginated cars
    const statsCars = allCars ?? cars;

    // Calculate statistics by shipper/port (exclude related cars)
    const statisticsTableData = useMemo(() => {
        const grouped = new Map<string, {
            code: string;
            name: string;
            total: number;
            collect: number;
            prepaid: number;
            car: number;
            van: number;
            bigVan: number;
            truck: number;
        }>();

        // Filter out related cars (cars with attachment_status)
        const mainCars = statsCars.filter(car => !car.attachment_status);

        mainCars.forEach(car => {
            const key = statisticsView === 'shippers'
                ? (car.shipper_id || 'no-shipper')
                : car.port_id;

            const code = statisticsView === 'shippers'
                ? (car.shipper?.code || 'N/A')
                : (car.port?.code || 'N/A');

            const name = statisticsView === 'shippers'
                ? (car.shipper?.shortname || car.shipper?.name || 'No Shipper')
                : (car.port?.port || 'No Port');

            if (!grouped.has(key)) {
                grouped.set(key, {
                    code,
                    name,
                    total: 0,
                    collect: 0,
                    prepaid: 0,
                    car: 0,
                    van: 0,
                    bigVan: 0,
                    truck: 0,
                });
            }

            const stats = grouped.get(key)!;
            stats.total++;

            if (car.prepaid_or_collect === 'C') {
                stats.collect++;
            } else if (car.prepaid_or_collect === 'P') {
                stats.prepaid++;
            }

            const carType = car.category ?? car.model?.type;
            if (carType === 'P') stats.car++;
            else if (carType === 'B') stats.van++;
            else if (carType === 'G') stats.bigVan++;
            else if (carType === 'L') stats.truck++;
        });

        return Array.from(grouped.values());
    }, [statsCars, statisticsView]);

    const sortedStatsData = useMemo(() => {
        return [...statisticsTableData].sort((a, b) => {
            const aVal = a[statsSortKey as keyof typeof a];
            const bVal = b[statsSortKey as keyof typeof b];
            const cmp = typeof aVal === 'string'
                ? aVal.localeCompare(bVal as string)
                : (aVal as number) - (bVal as number);
            return statsSortDir === 'asc' ? cmp : -cmp;
        });
    }, [statisticsTableData, statsSortKey, statsSortDir]);

    // Calculate line meter statistics (category L units only)
    const lineMeterTableData = useMemo(() => {
        const grouped = new Map<string, {
            code: string;
            name: string;
            units: number;
            length: number;
            weight: number;
            cbm: number;
        }>();

        // Filter to only category L cars (excluding related cars)
        const categoryLCars = statsCars.filter(car =>
            !car.attachment_status &&
            car.category === 'L'
        );

        categoryLCars.forEach(car => {
            const key = car.shipper_id || 'no-shipper';
            const code = car.shipper?.code || 'N/A';
            const name = car.shipper?.shortname || car.shipper?.name || 'No Shipper';

            if (!grouped.has(key)) {
                grouped.set(key, {
                    code,
                    name,
                    units: 0,
                    length: 0,
                    weight: 0,
                    cbm: 0,
                });
            }

            const stats = grouped.get(key)!;
            stats.units++;

            // Use car.length, fallback to model.length
            const carLength = car.length ?? car.model?.length ?? 0;
            stats.length += carLength;

            // Use car.weight, fallback to model.weight
            const carWeight = car.weight ?? car.model?.weight ?? 0;
            stats.weight += carWeight;

            // Use car.cbm, fallback to model.cbm
            const carCbm = car.cbm ?? car.model?.cbm ?? 0;
            stats.cbm += carCbm;
        });

        return Array.from(grouped.values());
    }, [statsCars]);

    const sortedLineMeterData = useMemo(() => {
        return [...lineMeterTableData].sort((a, b) => {
            const aVal = a[statsSortKey as keyof typeof a];
            const bVal = b[statsSortKey as keyof typeof b];
            if (aVal === undefined || bVal === undefined) return 0;
            const cmp = typeof aVal === 'string'
                ? aVal.localeCompare(bVal as string)
                : (aVal as number) - (bVal as number);
            return statsSortDir === 'asc' ? cmp : -cmp;
        });
    }, [lineMeterTableData, statsSortKey, statsSortDir]);

    const handleStatsSort = (key: string) => {
        if (statsSortKey === key) {
            setStatsSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setStatsSortKey(key);
            setStatsSortDir('asc');
        }
    };

    // Server-side filter options for cars in this voyage (dynamic based on other active filters)
    const getFilterOptions = useCallback(async (columnKey: string, currentFilters?: Record<string, string[]>): Promise<FilterOption[]> => {
        if (!voyageId) return [];
        try {
            return await carService.getFilterOptions(columnKey, currentFilters, { voyage_id: voyageId });
        } catch (error) {
            console.error('Error fetching filter options:', error);
            return [];
        }
    }, [voyageId]);

    // Handle filter changes from CarsManagementView - just update state, useEffect will trigger reload
    const handleFiltersChange = useCallback((filters: Record<string, string[]>) => {
        setColumnFilters(filters);
    }, [setColumnFilters]);

    // Reload cars when filters change (after initial load)
    const filtersInitializedRef = useRef(false);
    useEffect(() => {
        // Skip the first render (initial load is handled by loadVoyageAndCars)
        if (!filtersInitializedRef.current) {
            filtersInitializedRef.current = true;
            return;
        }
        // Only reload if voyage is loaded
        if (voyage && voyageId) {
            loadCarsOnly(carsSortColumn || 'ref_no', carsSortDirection, undefined, undefined, columnFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnFilters]);

    const handlePrintBookingConfirmation = async (selectedCars: Car[], options: { includeRelated: boolean } = { includeRelated: true }) => {
        if (selectedCars.length === 0) {
            showToast('No cars selected', 'warning');
            return;
        }

        setGeneratingPdf(true);
        try {
            const firstCar = selectedCars[0];
            const bookingNumber = firstCar?.booking_reference || '';
            const originalBooking = firstCar?.original_booking;

            const polTerminal = originalBooking?.port_terminal || '';
            const polAddress = originalBooking?.port_address || '';
            const polName = originalBooking?.port_name || '';
            const polRemarks = originalBooking?.port_remarks || '';
            const pol = polName;

            // Build the 9-row car entries with proper structure matching BookingConfirmationPage
            const carEntries: Array<{
                nr: number;
                description: string;
                vinSerial: string;
                destination: string;
                relatedCars?: Array<{
                    relationshipType: string;
                    makeModel: string;
                    vinSerial: string;
                }>;
            }> = [];

            let totalUnits = 0;
            for (const car of selectedCars) {
                if (carEntries.length >= 9) break;

                const dbMake = car.make?.name || '';
                const dbModel = car.model?.name || '';
                // Use database make/model if available, otherwise use manual_make_model
                const makeModel = dbMake && dbModel
                    ? `${dbMake} ${dbModel}`
                    : (dbMake || dbModel || car.manual_make_model || '');

                // Build related cars array if includeRelated is true
                let relatedCarsInfo: Array<{
                    relationshipType: string;
                    makeModel: string;
                    vinSerial: string;
                }> | undefined;

                if (options.includeRelated && car.related_cars && car.related_cars.length > 0) {
                    // Sort: Attached first, then STC
                    const sortedRelatedCars = [...car.related_cars].sort((a, b) => {
                        if (a.relationship_type === 'Attached' && b.relationship_type !== 'Attached') return -1;
                        if (a.relationship_type !== 'Attached' && b.relationship_type === 'Attached') return 1;
                        return 0;
                    });

                    relatedCarsInfo = sortedRelatedCars.map(rc => {
                        const rcDbMake = rc.make?.name || '';
                        const rcDbModel = rc.model?.name || '';
                        // Use database make/model if available, otherwise use manual_make_model
                        const rcMakeModel = rcDbMake && rcDbModel
                            ? `${rcDbMake} ${rcDbModel}`
                            : (rcDbMake || rcDbModel || rc.manual_make_model || '-');
                        return {
                            relationshipType: rc.relationship_type || 'STC',
                            makeModel: rcMakeModel.trim() || '-',
                            vinSerial: rc.chassis_number
                        };
                    });
                    totalUnits += relatedCarsInfo.length;
                }

                carEntries.push({
                    nr: carEntries.length + 1,
                    description: makeModel.trim() || '-',
                    vinSerial: car.chassis_number,
                    destination: car.port?.port || car.port?.code || '',
                    relatedCars: relatedCarsInfo
                });
                totalUnits++;
            }

            // Fill remaining slots with empty entries
            while (carEntries.length < 9) {
                carEntries.push({
                    nr: carEntries.length + 1,
                    description: '',
                    vinSerial: '',
                    destination: ''
                });
            }

            const filename = `Booking Confirmation ${pol || 'Unknown'} ${bookingNumber || 'N/A'}.pdf`;

            await printService.generateBookingConfirmation({
                bookingNumber: bookingNumber || undefined,
                terminal: polTerminal,
                date: formatDateOrToday(firstCar?.created_at),
                pol: pol,
                terminalAddress: polAddress,
                cars: carEntries,
                portRemarks: polRemarks || undefined
            }, filename);

            showToast(`Booking Confirmation generated for ${totalUnits} unit${totalUnits !== 1 ? 's' : ''}`, 'success');
        } catch (error) {
            console.error('Error generating booking confirmation:', error);
            showToast('Failed to generate booking confirmation', 'error');
        } finally {
            setGeneratingPdf(false);
        }
    };

    const generateStatsPDF = async () => {
        if (!voyage) return;

        setGeneratingPdf(true);

        try {
            if (statisticsView === 'lineMeter') {
                const filename = `Voyage Statistics ${voyage.voyage_code} By Line Meter.pdf`;
                await printService.generateLineMeterStatistics({
                    voyageCode: voyage.voyage_code,
                    vesselName: voyage.vessel_description,
                    date: new Date().toLocaleDateString('en-GB'),
                    data: sortedLineMeterData
                }, filename);
            } else {
                const viewLabel = statisticsView === 'shippers' ? 'Shippers' : 'Ports';
                const filename = `Voyage Statistics ${voyage.voyage_code} By ${viewLabel}.pdf`;
                await printService.generateVoyageStatistics({
                    voyageCode: voyage.voyage_code,
                    vesselName: voyage.vessel_description,
                    date: new Date().toLocaleDateString('en-GB'),
                    viewType: statisticsView,
                    data: sortedStatsData
                }, filename);
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading voyage details...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!voyage) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <p className="text-red-600 mb-4">Voyage not found</p>
                        <Button onClick={handleBackToPrevious}>Back</Button>
                    </div>
                </div>
            </Layout>
        );
    }

    const keyboardShortcuts = [
        {key: 'N', label: 'New'},
        {key: 'D', label: 'Delete'}
    ];

    const headerContent = (
        <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4 mb-4">
                    <button
                        onClick={handleBackToPrevious}
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                        {voyage.is_booking ? 'Booking' : 'Voyage'}: {voyage.voyage_code}
                    </h1>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                    <div className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-400 mb-1">Vessel:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
              {voyage.vessel_description}
            </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-400 mb-1">Total Cars:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
              {totalCars}
            </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-400 mb-1">Loaded:</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">
              {voyage.total_loaded}
            </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-400 mb-1">Departure:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatDateOrToday(voyage.departure)}
            </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-gray-600 dark:text-gray-400 mb-1">Currency:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
              {voyage.currency}
            </span>
                    </div>
                </div>
            </div>

            <KeyboardShortcuts shortcuts={keyboardShortcuts}/>
        </div>
    );

    return (
        <Layout fullWidth>
            <div className="space-y-6 px-3 py-4 sm:px-4 lg:px-6">
                {/* Header Card with Tabs */}
                <div
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                    <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                        {headerContent}
                    </div>

                    {/* Tabs */}
                    <div className="px-4 sm:px-6 py-2 bg-gray-50 dark:bg-gray-900/50">
                        <div className="flex space-x-1">
                            <button
                                onClick={() => setActiveTab('cars')}
                                className={`px-4 py-2 rounded-md font-medium transition-all text-sm ${
                                    activeTab === 'cars'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                Cars
                            </button>
                            <button
                                onClick={() => setActiveTab('statistics')}
                                className={`px-4 py-2 rounded-md font-medium transition-all text-sm ${
                                    activeTab === 'statistics'
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-transparent text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                }`}
                            >
                                Statistics
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'cars' ? (
                    <CarsManagementView
                        headerContent={null}
                        cars={cars}
                        loading={carsLoading}
                        showVoyageColumn={false}
                        keyboardShortcuts={undefined}
                        onAddCar={handleAddCarEntry}
                        onBulkAdd={() => navigate(`/bulk-add-cars/${voyageId}`)}
                        onEditCar={handleEdit}
                        onDeleteCar={handleDelete}
                        onRowDoubleClick={handleCarDoubleClick}
                        onBulkDelete={handleBulkDelete}
                        onBulkChangeVoyage={handleChangeVoyage}
                        onBulkChangeConsignee={handleChangeConsignee}
                        onBulkChangeDestination={handleChangeDestination}
                        onBulkUpdatePricing={handleUpdatePricing}
                        onBulkSetLengthPricing={handleSetLengthPricing}
                        onBulkSetLoaded={handleBulkSetLoaded}
                        onBulkCombineBl={handleCombineBl}
                        onBulkSeparateBl={handleSeparateBl}
                        onBulkAddInsurance={handleAddInsurance}
                        onBulkRemoveInsurance={handleRemoveInsurance}
                        onToggleLoaded={handleToggleLoaded}
                        onPrintBookingConfirmation={handlePrintBookingConfirmation}
                        onRefresh={loadVoyageAndCars}
                        loadingLoadedIds={Array.from(updatingCarIds)}
                        emptyMessage="No cars found for this voyage"
                        sortColumn={carsSortColumn}
                        sortDirection={carsSortDirection}
                        onSort={handleCarsSort}
                        totalCars={totalCars}
                        hasMore={hasMoreCars}
                        loadingMore={loadingMore}
                        onLoadMore={loadMoreCars}
                        voyageId={voyageId}
                        scrollToCarId={scrollToCarId}
                        onSearch={handleCarsSearch}
                        getFilterOptions={getFilterOptions}
                        onFiltersChange={handleFiltersChange}
                        columnFilters={columnFilters}
                        tableId={voyageId ? `cars-table-${voyageId}` : undefined}
                    />
                ) : (
                    <div
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Voyage
                                    Statistics</h2>
<button
                                    onClick={generateStatsPDF}
                                    disabled={generatingPdf || (statisticsView === 'lineMeter' ? sortedLineMeterData.length === 0 : sortedStatsData.length === 0)}
                                    className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors"
                                >
                                    {generatingPdf ? 'Generating...' : 'Save as PDF'}
                                </button>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => { setStatisticsView('shippers'); setStatsSortKey('name'); }}
                                    className={`px-4 py-2 rounded-md font-medium transition-all text-sm ${
                                        statisticsView === 'shippers'
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    By Shippers
                                </button>
                                <button
                                    onClick={() => { setStatisticsView('ports'); setStatsSortKey('name'); }}
                                    className={`px-4 py-2 rounded-md font-medium transition-all text-sm ${
                                        statisticsView === 'ports'
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    By Ports
                                </button>
                                <button
                                    onClick={() => { setStatisticsView('lineMeter'); setStatsSortKey('name'); }}
                                    className={`px-4 py-2 rounded-md font-medium transition-all text-sm ${
                                        statisticsView === 'lineMeter'
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    By Line Meter
                                </button>
                            </div>
                        </div>

                        {allCarsLoading && (
                            <div className="flex items-center gap-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                                </svg>
                                Loading all cars for statistics...
                            </div>
                        )}

                        {statisticsView === 'lineMeter' ? (
                        <div>
                            {/* Summary toggle */}
                            <div className="flex items-center gap-2 mb-4">
                                <button
                                    onClick={() => setLineMeterSummaryOnly(!lineMeterSummaryOnly)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all ${
                                        lineMeterSummaryOnly
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                    title={lineMeterSummaryOnly ? 'Show detailed table' : 'Show summary only'}
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {lineMeterSummaryOnly ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                                        )}
                                    </svg>
                                    {lineMeterSummaryOnly ? 'Summary' : 'Detailed'}
                                </button>
                            </div>

                            {lineMeterSummaryOnly ? (
                                /* Summary Card View */
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                                        <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">Total Units</div>
                                        <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.units, 0)}
                                        </div>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                                        <div className="text-sm text-green-600 dark:text-green-400 font-medium mb-1">Total Length</div>
                                        <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.length, 0).toFixed(2)} m
                                        </div>
                                    </div>
                                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                                        <div className="text-sm text-purple-600 dark:text-purple-400 font-medium mb-1">Total Weight</div>
                                        <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.weight, 0).toLocaleString()} kg
                                        </div>
                                    </div>
                                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                                        <div className="text-sm text-orange-600 dark:text-orange-400 font-medium mb-1">Total CBM</div>
                                        <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.cbm, 0).toFixed(3)}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                            /* Detailed Table View */
                            <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <SortableHeader sortKey="" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>Voyage</SortableHeader>
                                    <SortableHeader sortKey="code" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>Code</SortableHeader>
                                    <SortableHeader sortKey="name" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>Shipper</SortableHeader>
                                    <SortableHeader sortKey="units" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Units</SortableHeader>
                                    <SortableHeader sortKey="length" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Length (m)</SortableHeader>
                                    <SortableHeader sortKey="weight" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Weight (kg)</SortableHeader>
                                    <SortableHeader sortKey="cbm" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">CBM</SortableHeader>
                                </tr>
                                </thead>
                                <tbody
                                    className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedLineMeterData.length === 0 ? (
                                    <tr>
                                        <td colSpan={7}
                                            className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            No category L units found
                                        </td>
                                    </tr>
                                ) : (
                                    sortedLineMeterData.map((row, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {voyage?.voyage_code || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.code}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.name}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.units}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.length.toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.weight.toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.cbm.toFixed(3)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                                {lineMeterTableData.length > 0 && (
                                    <tr className="bg-gray-50 dark:bg-gray-900/50 font-semibold">
                                        <td colSpan={3}
                                            className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            Total
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.units, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.length, 0).toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.weight, 0).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {lineMeterTableData.reduce((sum, row) => sum + row.cbm, 0).toFixed(3)}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                            )}
                        </div>
                        ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-900/50">
                                <tr>
                                    <SortableHeader sortKey="" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>Voyage</SortableHeader>
                                    <SortableHeader sortKey="code" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>Code</SortableHeader>
                                    <SortableHeader sortKey="name" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort}>
                                        {statisticsView === 'shippers' ? 'Shipper' : 'Port'}
                                    </SortableHeader>
                                    <SortableHeader sortKey="total" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Total</SortableHeader>
                                    <SortableHeader sortKey="collect" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Collect</SortableHeader>
                                    <SortableHeader sortKey="prepaid" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Prepaid</SortableHeader>
                                    <SortableHeader sortKey="car" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Car (P)</SortableHeader>
                                    <SortableHeader sortKey="van" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Van (B)</SortableHeader>
                                    <SortableHeader sortKey="bigVan" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">BigVan (G)</SortableHeader>
                                    <SortableHeader sortKey="truck" currentSort={statsSortKey} sortDir={statsSortDir}
                                                    onSort={handleStatsSort} align="right">Truck (L)</SortableHeader>
                                </tr>
                                </thead>
                                <tbody
                                    className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedStatsData.length === 0 ? (
                                    <tr>
                                        <td colSpan={10}
                                            className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    sortedStatsData.map((row, index) => (
                                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {voyage?.voyage_code || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.code}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.name}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.total}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.collect}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.prepaid}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.car}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.van}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.bigVan}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                                {row.truck}
                                            </td>
                                        </tr>
                                    ))
                                )}
                                {statisticsTableData.length > 0 && (
                                    <tr className="bg-gray-50 dark:bg-gray-900/50 font-semibold">
                                        <td colSpan={3}
                                            className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            Total
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.total, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.collect, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.prepaid, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.car, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.van, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.bigVan, 0)}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0">
                                            {statisticsTableData.reduce((sum, row) => sum + row.truck, 0)}
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                )}
            </div>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={hideToast}
                />
            )}
        </Layout>
    );
};

const SortableHeader: React.FC<{
    sortKey: string;
    currentSort: string;
    sortDir: 'asc' | 'desc';
    onSort: (key: string) => void;
    align?: 'left' | 'right';
    children: React.ReactNode;
}> = ({sortKey, currentSort, sortDir, onSort, align = 'left', children}) => {
    const isActive = sortKey === currentSort && sortKey !== '';
    return (
        <th
            onClick={() => sortKey && onSort(sortKey)}
            className={`px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 last:border-r-0 ${
                sortKey ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none' : ''
            } ${align === 'right' ? 'text-right' : 'text-left'}`}
        >
      <span className="inline-flex items-center gap-1">
        {children}
          {isActive && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
        </th>
    );
};

export default VoyageDetailsPage;
